import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import { setEventSpaceID, clearEventSpaceID } from "./eventspaceSlice";
import {
  ActiveInactiveEventSpace,
  deleteEventSpace,
  getAllEventSpace,
} from "./services/eventspace.service";
import AvatarComponent from "../../components/common/AvatarComponent";

import { useNavigate, useLocation } from "react-router-dom";
import { FiEdit } from "react-icons/fi";

const EventSpace: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    dispatch(getAllEventSpace({ EventSpaceTitle: "" }));
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const handleCreate = () => {
    navigate("/eventspace/create");
  };

  return (
    <>
      <DeleteEventSpaceDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Event Space List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              {"Add New Event Space"}
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <EventSpaceGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const EventSpaceGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const EventSpaceList = useAppSelector(
    (state) => state.eventspace.EventSpaceList,
  );
  const loading = useAppSelector((state) => state.eventspace.loading);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? EventSpaceList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteEventSpace"));
      dispatch(setEventSpaceID(ID));
    };

    const handleEdit = (ID: number) => {
      navigate("/eventspace/edit", { state: { EventSpaceID: ID } });
    };

    return (
      <td
        className="action-td"
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(props.dataItem.ID)}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 1 || props.index === 2 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyActiveInActtiveCommandCell = (props: GridCellProps) => {
    const [IsActive, setIsActive] = useState<boolean>(
      props.dataItem.IsActive ?? false,
    );

    const handleActiveInActive = () => {
      const newStatus = !IsActive;
      setIsActive(newStatus);
      const data = {
        ID: props.dataItem.ID,
        IsActive: newStatus,
      };
      dispatch(ActiveInactiveEventSpace(data)).then((response: any) => {
        if (response.payload?.Status === 200) {
          dispatch(getAllEventSpace({ EventSpaceTitle: "" }));
        }
      });
    };

    return (
      <td
        style={{ textAlign: "center", ...props.style }}
        className={props.className}
      >
        <Switch
          disabled={!isEditAccess}
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {EventSpaceList && EventSpaceList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          filterable={true}
          filter={filter}
          className="responsive-table"
          data={
            filter
              ? filterBy(EventSpaceList, filter).slice(
                  page.skip,
                  page.take + page.skip,
                )
              : EventSpaceList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={EventSpaceList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="EventSpaceTitle"
            title="Event Space Title"
            cells={{
              data: (props: any) => (
                <td>
                  <div>
                    {isEditAccess ? (
                      <div
                        className="hoverableName"
                        onClick={() =>
                          navigate("/eventspace/edit", {
                            state: { EventSpaceID: props.dataItem?.ID },
                          })
                        }
                      >
                        {props.dataItem?.EventSpaceTitle}
                      </div>
                    ) : (
                      <div className="normalName">
                        {props.dataItem?.EventSpaceTitle}
                      </div>
                    )}
                  </div>
                </td>
              ),
            }}
          />
          <Column field="GuestCapacity" title="Capacity" />
          <Column field="Area" title="Area" />
          <Column
            field="Images"
            title="Image"
            width={110}
            cells={{
              data: (props: any) => {
                const firstImage = props.dataItem?.Images?.split(",")[0];
                return (
                  <td>
                    <AvatarComponent
                      image={firstImage}
                      title={props.dataItem?.EventSpaceTitle}
                    />
                  </td>
                );
              },
            }}
          />
          <Column
            field="ID"
            title="Status"
            cells={{ data: MyActiveInActtiveCommandCell }}
            width={"90px"}
            filterable={false}
            locked={true}
          />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cells={{ data: MyEditCommandCell }}
              width={"110px"}
              locked={true}
              filterable={false}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteEventSpaceDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.eventspace.loading);
  const EventSpaceID = useAppSelector((state) => state.eventspace.EventSpaceID);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteEventSpace(ID));
      if (response.payload?.Status === 200) {
        dispatch(getAllEventSpace({ EventSpaceTitle: "" }));
        dispatch(closeDialog());
        dispatch(clearEventSpaceID());
      } else {
        dispatch(getAllEventSpace({ EventSpaceTitle: "" }));
        dispatch(closeDialog());
        dispatch(clearEventSpaceID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteEventSpace" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Event Space"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Event Space?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                  gap: 10,
                }}
              >
                <DialogCloseButton themeColor="dark" fillMode="flat" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(EventSpaceID)}
                  type="button"
                  disabled={loading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default EventSpace;
